import { api } from './client';
import {
  DEFAULT_CHUNK_PLAINTEXT_BYTES,
  encryptBlobInChunks,
  type ChunkedFileManifest,
} from '../crypto/chunkedFile';
import { beginUploadGuard, endUploadGuard } from './uploadGuard';

export interface BlobUploadProgress {
  phase: 'encrypting' | 'uploading';
  bytesCompleted: number;
  bytesTotal: number;
}

export interface UploadBlobOptions {
  chunkSizeBytes?: number;
  onProgress?: (progress: BlobUploadProgress) => void;
}

export interface EncryptAndUploadBlobOptions {
  fileId: string;
  contentKey?: Uint8Array;
  chunkPlaintextBytes?: number;
  onProgress?: (progress: BlobUploadProgress) => void;
}

async function readSourceSlice(
  source: Uint8Array | Blob,
  start: number,
  end: number,
): Promise<Uint8Array> {
  if (source instanceof Blob) {
    return new Uint8Array(await source.slice(start, end).arrayBuffer());
  }

  return source.subarray(start, end);
}

function sourceSize(source: Uint8Array | Blob): number {
  return source instanceof Blob ? source.size : source.byteLength;
}

async function uploadedIndexSet(uploadId: string): Promise<Set<number>> {
  const status = await api.getBlobUploadStatus(uploadId);
  return new Set(status.uploadedChunkIndices);
}

async function putChunkWithResume(
  uploadId: string,
  chunkIndex: number,
  slice: Uint8Array,
  onProgress?: (loaded: number, total: number) => void,
  knownUploaded?: Set<number>,
): Promise<Set<number>> {
  let uploaded = knownUploaded ?? (await uploadedIndexSet(uploadId));
  if (uploaded.has(chunkIndex)) {
    onProgress?.(slice.byteLength, slice.byteLength);
    return uploaded;
  }

  try {
    await api.uploadBlobChunk(uploadId, chunkIndex, slice, onProgress);
    uploaded.add(chunkIndex);
    return uploaded;
  } catch {
    uploaded = await uploadedIndexSet(uploadId);
    if (uploaded.has(chunkIndex)) {
      onProgress?.(slice.byteLength, slice.byteLength);
      return uploaded;
    }

    await api.uploadBlobChunk(uploadId, chunkIndex, slice, onProgress);
    uploaded.add(chunkIndex);
    return uploaded;
  }
}

export async function uploadBlob(
  source: Uint8Array | Blob,
  options: UploadBlobOptions = {},
): Promise<string> {
  beginUploadGuard();
  try {
    const started = await api.startBlobUpload();
    const size = sourceSize(source);
    const effectiveChunkSize = options.chunkSizeBytes ?? started.maxChunkSizeBytes;
    const chunkCount = size === 0 ? 1 : Math.ceil(size / effectiveChunkSize);
    let bytesSent = 0;
    let uploaded = await uploadedIndexSet(started.uploadId);

    for (let index = 0; index < chunkCount; index++) {
      const offset = index * effectiveChunkSize;
      const end = size === 0 ? 0 : Math.min(offset + effectiveChunkSize, size);
      const slice = await readSourceSlice(source, offset, end);
      uploaded = await putChunkWithResume(
        started.uploadId,
        index,
        slice,
        (loaded, total) => {
          const sliceProgress = total > 0 ? loaded / total : 1;
          options.onProgress?.({
            phase: 'uploading',
            bytesCompleted: bytesSent + Math.round(sliceProgress * (end - offset)),
            bytesTotal: size,
          });
        },
        uploaded,
      );
      bytesSent = end;
      options.onProgress?.({
        phase: 'uploading',
        bytesCompleted: bytesSent,
        bytesTotal: size,
      });
    }

    const completed = await api.completeBlobUpload(started.uploadId, chunkCount);
    return completed.blobId;
  } finally {
    endUploadGuard();
  }
}

export async function encryptAndUploadBlob(
  blob: Blob,
  options: EncryptAndUploadBlobOptions,
): Promise<{ blobId: string; manifest: ChunkedFileManifest }> {
  beginUploadGuard();
  try {
    const started = await api.startBlobUpload();
    const httpChunkSize = started.maxChunkSizeBytes;
    let httpIndex = 0;
    let uploaded = await uploadedIndexSet(started.uploadId);

    const { manifest } = await encryptBlobInChunks(
      options.fileId,
      blob,
      options.chunkPlaintextBytes ?? DEFAULT_CHUNK_PLAINTEXT_BYTES,
      {
        contentKey: options.contentKey,
        collectChunks: false,
        onProgress: (bytesCompleted, bytesTotal) => {
          options.onProgress?.({
            phase: 'encrypting',
            bytesCompleted,
            bytesTotal,
          });
        },
        onChunk: async (chunk, range) => {
          const packed = chunk.ciphertext;
          const sliceCount = packed.length === 0 ? 1 : Math.ceil(packed.length / httpChunkSize);
          const slicePlaintext = Math.max(0, range.end - range.start);

          for (let index = 0; index < sliceCount; index++) {
            const start = index * httpChunkSize;
            const end = Math.min(start + httpChunkSize, packed.length);
            const slice = packed.subarray(start, end);
            const chunkIndex = httpIndex;
            uploaded = await putChunkWithResume(
              started.uploadId,
              chunkIndex,
              slice,
              (loaded, total) => {
                const fraction = total > 0 ? loaded / total : 1;
                const httpFraction = (index + fraction) / sliceCount;
                options.onProgress?.({
                  phase: 'uploading',
                  bytesCompleted: range.start + Math.round(httpFraction * slicePlaintext),
                  bytesTotal: blob.size,
                });
              },
              uploaded,
            );
            httpIndex += 1;
          }

          options.onProgress?.({
            phase: 'uploading',
            bytesCompleted: range.end,
            bytesTotal: blob.size,
          });
        },
      },
    );

    const completed = await api.completeBlobUpload(started.uploadId, Math.max(httpIndex, 1));
    return { blobId: completed.blobId, manifest };
  } finally {
    endUploadGuard();
  }
}
